import { create } from 'zustand';
import { apiGet, apiPost, setAccessToken, setRefreshToken, getRefreshToken, tryRefresh } from './api';

/**
 * Minimal auth store. UI state only — server truth lives on the API.
 * Session shape: current user + role + tenant. The access token itself is in `api.ts` memory.
 *
 * Multi-tenant identity (F-Step 104): student memberships travel alongside the User. When the
 * feature flag is on, `activeTenantId` scopes every list query; the header workspace switcher
 * flips it via `POST /auth/session/switch-workspace`.
 */

export type MembershipRelationship =
  | 'full_time'
  | 'part_time'
  | 'alumni'
  | 'public'
  | 'dual_degree'
  | 'intern'
  | 'staff'
  | 'other';

export interface Membership {
  tenantId: string;
  tenantName: string;
  relationship: MembershipRelationship;
  primary: boolean;
  status: 'imported' | 'pending' | 'verified' | 'rejected' | 'ended';
}

export interface AuthUser {
  id: string;
  fullName: string;
  email: string;
  role: string;
  tenant: string;
  onboardingComplete?: boolean;
  /** Server-issued permission slugs; UI uses these to gate action buttons. */
  permissions?: string[];
  college?: string;
  department?: string;
  partner?: string;
}

interface AuthState {
  user: AuthUser | null;
  activeTenantId: string | null;
  memberships: Membership[];
  loading: boolean;
  init: () => Promise<void>;
  setSession: (
    u: AuthUser,
    tokens: { accessToken: string; refreshToken: string },
    session?: { activeTenantId?: string | null; memberships?: Membership[] },
  ) => void;
  setActiveTenantId: (id: string) => void;
  updateAccessToken: (token: string) => void;
  setMemberships: (m: Membership[]) => void;
  logout: () => Promise<void>;
}

interface MeResponse {
  user: AuthUser;
  activeTenantId?: string | null;
  memberships?: Membership[];
}

export const useAuthStore = create<AuthState>((set, _get) => ({
  user: null,
  activeTenantId: null,
  memberships: [],
  loading: true,

  init: async () => {
    if (!getRefreshToken()) {
      set({ loading: false });
      return;
    }
    try {
      // F-074: use the shared `tryRefresh` singleton — the response interceptor uses the same
      // one, so any parallel request-triggered refresh in flight gets awaited and reused,
      // preventing a second refresh call that would trigger reuse-detection on the server.
      const newAccess = await tryRefresh();
      if (!newAccess) {
        set({ user: null, loading: false });
        return;
      }
      // Access token is already set by tryRefresh; hydrate `me` with the new bearer.
      const me = await apiGet<MeResponse>('/auth/me').catch(() => null);
      set({
        user: me?.user ?? null,
        activeTenantId: me?.activeTenantId ?? me?.user?.tenant ?? null,
        memberships: me?.memberships ?? [],
        loading: false,
      });
    } catch {
      set({ loading: false, user: null, activeTenantId: null, memberships: [] });
    }
  },

  setSession: (user, tokens, session) => {
    setAccessToken(tokens.accessToken);
    setRefreshToken(tokens.refreshToken);
    set({
      user,
      activeTenantId: session?.activeTenantId ?? user.tenant ?? null,
      memberships: session?.memberships ?? [],
    });
  },

  setActiveTenantId: (id) => set({ activeTenantId: id }),

  updateAccessToken: (token) => {
    setAccessToken(token);
  },

  setMemberships: (memberships) => set({ memberships }),

  logout: async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await apiPost('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    setAccessToken(null);
    setRefreshToken(null);
    set({ user: null, activeTenantId: null, memberships: [] });
  },
}));

/** Convenience selectors — keep call-sites terse. */
export const useActiveTenantId = () => useAuthStore((s) => s.activeTenantId);
export const useMemberships = () => useAuthStore((s) => s.memberships);
